import { Button, Checkbox, Row, Table, Space, Popconfirm, Tooltip } from 'antd';
import React, { useState, useEffect } from 'react';
import AuthorizedPersonService from '../../services/AuthorizedPersonService';
import CompanyService from '../../services/CompanyService';
import AuthorizedPersonDrawer from './AuthorizedPersonDrawer';
import { capitalizeSentences, openNotification, sortAuthorizedPersonsByName } from '../../utils'; // Varsayım: Bildirim için
import { notificationTypeLabels, sortNotificationTypes } from '../../constants/notificationTypes';
import PhoneNumberDisplay from '../PhoneNumberDisplay';

import TrashIcon from '../../assets/icons/trash.svg';
import PenIcon from '../../assets/icons/pen.svg';

interface AuthorizedPerson {
  id: number;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phoneNumber: string;
  default: boolean;
  notificationTypes?: string[];
  controlAddressIds?: number[];
}

interface ControlAddressInfo {
  id: number;
  title: string;
  addressLine1: string;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
}

type SelectionFilter = 'offerOnly' | 'planOnly' | 'hasAny' | 'none';

const AuthorizedPersonTable = ({
  companyId,
  setSelectedAuthorizedPersonIds,
  selectedAuthorizedPersonIds,
  controlAddressId,
  type = false,
  showSelection = true,
  selectionFilter,
  showSelectionMessage = true,
  readOnly = false,
}: {
  companyId: number;
  setSelectedAuthorizedPersonIds: (ids: number[]) => void;
  selectedAuthorizedPersonIds: number[];
  controlAddressId?: number | null;
  type?: boolean; // legacy flag used in proposal create/edit
  showSelection?: boolean;
  selectionFilter?: SelectionFilter; // override selection rule
  showSelectionMessage?: boolean; // controls hint visibility
  readOnly?: boolean;
}) => {
  const [authorizedPersons, setAuthorizedPersons] = useState<AuthorizedPerson[]>([]);
  const [controlAddressById, setControlAddressById] = useState<Record<number, ControlAddressInfo>>({});
  const [fetchedAuthorizedPersonsCompanyId, setFetchedAuthorizedPersonsCompanyId] = useState<number | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>(selectedAuthorizedPersonIds);

  // Drawer görünürlüğü ve düzenlenecek kişi
  const [isAuthorizedPersonDrawerVisible, setAuthorizedPersonDrawerVisible] = useState(false);
  const [editPerson, setEditPerson] = useState<AuthorizedPerson | null>(null);

  useEffect(() => {
    if (companyId) {
      fetchAuthorizedPersons();
      fetchControlAddressDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const normalizeSelectedAuthorizedPersonIds = (ids: unknown): number[] => {
    return Array.isArray(ids)
      ? Array.from(
          new Set(
            ids
              .map((id) => {
                const num = Number(id);
                return Number.isFinite(num) ? num : null;
              })
              .filter((id): id is number => id != null),
          ),
        )
      : [];
  };

  // Keep selection in sync while preserving already-selected ids (even if disabled)
  useEffect(() => {
    const sanitized = normalizeSelectedAuthorizedPersonIds(selectedAuthorizedPersonIds);
    const sameAsState =
      sanitized.length === selectedRows.length &&
      sanitized.every((id) => selectedRows.includes(id));
    if (!sameAsState) {
      setSelectedRows(sanitized);
    }
    if (sanitized.length !== selectedAuthorizedPersonIds.length) {
      setSelectedAuthorizedPersonIds(sanitized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAuthorizedPersonIds]);

  const effectiveFilter: SelectionFilter = selectionFilter ?? (type === true ? 'offerOnly' : 'none');
  const effectiveShowSelection = !readOnly && showSelection;
  const isControlAddressFilteringActive = controlAddressId !== undefined;
  const hasControlAddress = (person: AuthorizedPerson, addressId: number | string | null | undefined) => {
    if (addressId == null) {
      return false;
    }
    const targetId = Number(addressId);
    if (!Number.isFinite(targetId)) {
      return false;
    }
    const controlAddressIds = Array.isArray(person?.controlAddressIds) ? person.controlAddressIds : [];
    return controlAddressIds.some((id) => Number(id) === targetId);
  };

  const selectionHint = (() => {
    if (isControlAddressFilteringActive && controlAddressId == null) {
      return 'Yetkilileri listelemek için önce bir kontrol adresi seçiniz.';
    }
    switch (effectiveFilter) {
      case 'offerOnly':
        return 'Teklifin gönderileceği yetkilileri seçiniz.';
      case 'planOnly':
        return 'Plan gönderilecek yetkili kişileri seçiniz.';
      case 'hasAny':
        return 'İletişim konusu bulunan yetkilileri seçiniz.';
      default:
        return null;
    }
  })();

  useEffect(() => {
    if (!isControlAddressFilteringActive || controlAddressId == null) {
      return;
    }
    if (fetchedAuthorizedPersonsCompanyId !== companyId) {
      return;
    }

    const currentSelectedRows = normalizeSelectedAuthorizedPersonIds(selectedAuthorizedPersonIds);
    const sanitizedSelectedRows = currentSelectedRows.filter((id) => {
      const person = authorizedPersons.find((item) => item.id === id);
      if (!person) {
        return false;
      }
      return hasControlAddress(person, controlAddressId);
    });

    const sameAsState =
      sanitizedSelectedRows.length === selectedRows.length &&
      sanitizedSelectedRows.every((id) => selectedRows.includes(id));
    if (!sameAsState) {
      setSelectedRows(sanitizedSelectedRows);
    }
    if (sanitizedSelectedRows.length !== currentSelectedRows.length) {
      setSelectedAuthorizedPersonIds(sanitizedSelectedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlAddressId, authorizedPersons, selectedAuthorizedPersonIds, fetchedAuthorizedPersonsCompanyId, companyId]);

  const canSelect = (person: AuthorizedPerson) => {
    if (isControlAddressFilteringActive) {
      if (controlAddressId == null) {
        return false;
      }
      if (!hasControlAddress(person, controlAddressId)) {
        return false;
      }
    }

    const types = Array.isArray(person?.notificationTypes) ? person.notificationTypes : [];
    switch (effectiveFilter) {
      case 'offerOnly':
        return types.includes('OFFER');
      case 'planOnly':
        return types.includes('PLAN');
      case 'hasAny':
        return types.length > 0;
      case 'none':
      default:
        return true;
    }
  };

  const fetchAuthorizedPersons = async () => {
    try {
      setFetchedAuthorizedPersonsCompanyId(null);
      const response = await AuthorizedPersonService.getCompanyAuthorizedPersons(companyId);
      const authorizedList = (Array.isArray(response?.data) ? response.data : response?.data?.content ?? []) as AuthorizedPerson[];
      const sorted = sortAuthorizedPersonsByName<AuthorizedPerson>(authorizedList);
      setAuthorizedPersons(sorted);
      setFetchedAuthorizedPersonsCompanyId(companyId);
    } catch (error) {
      console.error('Yetkili kişiler yüklenirken hata oluştu:', error);
      openNotification('error', 'Hata', 'Yetkili kişiler yüklenemedi');
    }
  };

  const fetchControlAddressDetails = async () => {
    try {
      const response = await CompanyService.getCompany(companyId);
      const addresses = Array.isArray(response?.data?.addresses) ? response.data.addresses : [];
      const addressById = addresses
        .filter((address: any) => address?.type === 'CONTROL')
        .reduce((acc: Record<number, ControlAddressInfo>, address: any) => {
          const addressId = Number(address?.id);
          if (Number.isFinite(addressId)) {
            acc[addressId] = {
              id: addressId,
              title: String(address?.title || '').trim(),
              addressLine1: String(address?.addressLine1 || '').trim(),
              addressLine2: address?.addressLine2,
              city: address?.city,
              state: address?.state,
            };
          }
          return acc;
        }, {});

      setControlAddressById(addressById);
    } catch (error) {
      console.error('İlgili lokasyonlar yüklenirken hata oluştu:', error);
      setControlAddressById({});
    }
  };

  const formatControlAddressTitles = (controlAddressIds?: Array<number | string>) => {
    if (!Array.isArray(controlAddressIds) || controlAddressIds.length === 0) {
      return '-';
    }

    const titles = controlAddressIds
      .map((id) => controlAddressById[Number(id)]?.title)
      .filter(Boolean);

    return titles.length > 0 ? titles.join(', ') : '-';
  };

  const toTurkishUpperText = (value?: string | null) => String(value || '').trim().toLocaleUpperCase('tr-TR');

  const getControlAddressTooltip = (controlAddressIds?: Array<number | string>) => {
    const addresses = Array.isArray(controlAddressIds)
      ? controlAddressIds
        .map((id) => controlAddressById[Number(id)])
        .filter((address): address is ControlAddressInfo => Boolean(address))
      : [];

    if (addresses.length === 0) {
      return '-';
    }

    return (
      <div>
        {addresses.map((address, index) => {
          const addressText = [address.addressLine1, address.addressLine2].map(toTurkishUpperText).filter(Boolean).join(' ');
          const districtCity = [toTurkishUpperText(address.state), toTurkishUpperText(address.city)].filter(Boolean).join(' / ');

          return (
            <div key={address.id} style={{ marginBottom: index === addresses.length - 1 ? 0 : 8 }}>
              <div>{toTurkishUpperText(address.title) || '-'}</div>
              <div>{addressText || '-'}</div>
              <div>{districtCity || '-'}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Checkbox satır seçme
  const handleCheckboxChange = (record: AuthorizedPerson) => {
    if (readOnly) return;
    if (!canSelect(record)) return; // Prevent selecting when not allowed
    const updatedSelectedRows = selectedRows.includes(record.id)
      ? selectedRows.filter((id) => id !== record.id)
      : [...selectedRows, record.id];

    setSelectedRows(updatedSelectedRows);
    setSelectedAuthorizedPersonIds(updatedSelectedRows);
  };

  // Yeni Yetkili Ekle (Drawer'ı boş form ile açar)
  const handleAddPersonClick = () => {
    setEditPerson(null);
    setAuthorizedPersonDrawerVisible(true);
  };

  // Edit
  const handleEditPersonClick = (person: AuthorizedPerson) => {
    setEditPerson(person);
    setAuthorizedPersonDrawerVisible(true);
  };

  // Delete
  const handleDeletePerson = async (person: AuthorizedPerson) => {
    try {
      await AuthorizedPersonService.remove(person.id);
      openNotification('success', 'Başarılı', 'Yetkili başarıyla silindi');
      fetchAuthorizedPersons();
    } catch (error) {
      console.error('Yetkili silinirken hata oluştu:', error);
      openNotification('error', 'Hata', 'Yetkili silinemedi');
    }
  };

  // Drawer kapandığında listeyi yenile
  const handleAuthorizedPersonDrawerClose = () => {
    setAuthorizedPersonDrawerVisible(false);
    setEditPerson(null);
    fetchAuthorizedPersons();
  };

  const columns = [
    ...(effectiveShowSelection
      ? [
        {
          title: '',
          width: 50,
          key: 'selection',
          render: (_text: string, record: AuthorizedPerson) => {
            const disabled = !canSelect(record);
            const checkbox = (
              <Checkbox
                checked={selectedRows.includes(record.id)}
                onChange={() => handleCheckboxChange(record)}
                onClick={(event) => event.stopPropagation()}
                disabled={disabled}
              />
            );
            if (!disabled) return checkbox;

            // Tooltip for disabled state: reason by filter
            const types = Array.isArray(record?.notificationTypes) ? record.notificationTypes : [];
            const title = (() => {
              if (isControlAddressFilteringActive && controlAddressId == null) return 'Önce kontrol adresi seçilmelidir.';
              if (isControlAddressFilteringActive) {
                if (!hasControlAddress(record, controlAddressId)) return 'Seçilen KONTROL ADRESİ ile ilişkilendirilmemiştir.';
              }
              if (effectiveFilter === 'hasAny' && types.length === 0) return 'İletişim konusu yok';
              if (effectiveFilter === 'offerOnly' && !types.includes('OFFER')) return 'İletişim konularında TEKLİF seçimi bulunmamaktadır.';
              if (effectiveFilter === 'planOnly' && !types.includes('PLAN')) return 'İletişim konularında PLAN seçimi bulunmamaktadır.';
              return 'Seçilemez';
            })();

            return (
              <Tooltip title={title}>
                <span>{checkbox}</span>
              </Tooltip>
            );
          },
        },
      ]
      : []),
    {
      title: 'Yetkili Kişi',
      key: 'person',
      render: (_: any, record: any) => {
        const fullName = `${record.firstName ?? ''} ${record.lastName ?? ''}`;
        const position = record.position ?? '';

        return (
          <div>
            <div style={{ fontWeight: 500 }}>{fullName}</div>
            <Tooltip placement="topLeft" title={position}>
              <div style={{ color: '#888' }}>{capitalizeSentences(position)}</div>
            </Tooltip>
          </div>
        );
      },
      onCell: (record: AuthorizedPerson) => ({
        onClick: (event: React.MouseEvent<HTMLTableCellElement>) => {
          if ((event.target as HTMLElement).closest('input')) {
            return;
          }
          event.stopPropagation();
          if (effectiveShowSelection && canSelect(record)) {
            handleCheckboxChange(record);
          }
        },
        style: { cursor: effectiveShowSelection && canSelect(record) ? 'pointer' : 'default' },
      }),
    },
    {
      title: 'İletişim',
      key: 'contact',
      render: (_: any, record: any) => (
        <>
          <div>{record.email}</div>
          <PhoneNumberDisplay value={record.phoneNumber} />
        </>
      ),
    },
    {
      title: 'Adresler',
      dataIndex: 'controlAddressIds',
      key: 'controlAddressIds',
      render: (controlAddressIds: Array<number | string>) => {
        const titles = formatControlAddressTitles(controlAddressIds);
        return (
          <Tooltip placement="topLeft" title={getControlAddressTooltip(controlAddressIds)}>
            <span>{titles}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'İletişim Konuları',
      dataIndex: 'notificationTypes',
      key: 'notificationTypes',
      render: (types: string[]) => {
        if (!types || types.length === 0) return '-';
        const sorted = sortNotificationTypes(types as any);
        return sorted
          .map((t) => notificationTypeLabels[t as keyof typeof notificationTypeLabels])
          .join(', ');
      },
    },
    ...(!readOnly
      ? [
        {
          title: 'İşlem',
          key: 'action',
          render: (_text: any, record: AuthorizedPerson) => {
            const disableDelete = record?.default === true;
            const deleteButton = (
              <Button
                icon={<img src={TrashIcon} alt="trash" className="icon" />}
                shape="circle"
                className="icon-button"
                disabled={disableDelete}
                onClick={(e) => e.stopPropagation()} // Popconfirm açılmasını engellemeden satır seçimi durdurmak için
              />
            );

            return (
              <Space>
                {/* Düzenle */}
                <Button
                  icon={<img src={PenIcon} alt="pen" className="icon" />}
                  shape="circle"
                  className="icon-button"
                  onClick={(e) => {
                    e.stopPropagation(); // Tabloda satır seçimi engelle
                    handleEditPersonClick(record);
                  }}
                />

                {/* Sil */}
                {disableDelete ? (
                  <Tooltip title="Varsayılan yetkili silinemez">{deleteButton}</Tooltip>
                ) : (
                  <Popconfirm
                    placement="left"
                    title="Bu yetkiliyi silmek istediğinizden emin misiniz?"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleDeletePerson(record);
                    }}
                    okText="Evet"
                    cancelText="Hayır"
                    onCancel={(e) => e?.stopPropagation()}
                  >
                    {deleteButton}
                  </Popconfirm>
                )}
              </Space>
            );
          },
        },
      ]
      : []),
  ];

  return (
    <>
      <Row justify="space-between" align="middle" className="mb-16px">
        <h3 className='m-0 p-0'>Yetkili Kişiler</h3>
        {!readOnly && (
          <Button
            type="primary"
            className="d-button width-auto dark-button"
            onClick={handleAddPersonClick}
          >
            Yeni Yetkili Ekle
          </Button>
        )}
      </Row>

      {effectiveShowSelection && showSelectionMessage && selectionHint && (
        <div style={{ marginBottom: 16 }}>
          {selectionHint}
        </div>
      )}

      <Table
        scroll={{ x: 768 }}
        columns={columns}
        pagination={false}
        dataSource={authorizedPersons}
        rowKey={(record) => record.id.toString()}
        onRow={effectiveShowSelection ? (record) => ({
          onClick: () => canSelect(record) && handleCheckboxChange(record),
        }) : undefined}
      />

      {!readOnly && (
        <AuthorizedPersonDrawer
          visible={isAuthorizedPersonDrawerVisible}
          onClose={handleAuthorizedPersonDrawerClose}
          companyId={companyId as any}
          existingData={null}
          editPerson={editPerson}
        />
      )}
    </>
  );
};

export default AuthorizedPersonTable;
